/**
 * Clients data hooks with mock fallback.
 *
 * Reads degrade gracefully: when not authenticated, or when the API is
 * unreachable/errors, hooks return the mock store so the app stays usable.
 * Writes do NOT silently fall back — in API mode a failed mutation throws so the
 * caller can surface the error and keep the form open. The UI loads one page
 * (limit 100, ample for current data) and filters client-side for mock/API
 * parity; the API also supports server-side search/pagination (docs/api.md).
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CLIENTS_QUERY_PREFIX, queryKeys } from "@/lib/query-client";
import type { Client } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { clientsApi } from "./clients.api";
import { toClient } from "./clients.adapter";
import { useClientsStore } from "./clients.store";
import type { ClientFormValues } from "./components/ClientFormModal";

const LIST_PARAMS = { limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };

export type DataSource = "api" | "mock";

export interface UseClientsResult {
  clients: Client[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  source: DataSource;
  error: unknown;
  refetch: () => void;
}

export function useClients(): UseClientsResult {
  const status = useAuthStore((s) => s.status);
  const mockActive = useClientsStore((s) => s.active);
  const authed = status === "authenticated";

  const query = useQuery({
    queryKey: queryKeys.clientsList(LIST_PARAMS),
    queryFn: () => clientsApi.list(LIST_PARAMS),
    enabled: authed,
  });

  if (!authed) {
    return { clients: mockActive, isLoading: false, isError: false, isFetching: false, source: "mock", error: null, refetch: query.refetch };
  }
  if (query.data) {
    return { clients: query.data.data.map(toClient), isLoading: false, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
  }
  if (query.isError) {
    // API failed → fall back to mock data but report the error for a banner.
    return { clients: mockActive, isLoading: false, isError: true, isFetching: false, source: "mock", error: query.error, refetch: query.refetch };
  }
  return { clients: [], isLoading: query.isLoading, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
}

export interface UseClientResult {
  client: Client | undefined;
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  error: unknown;
}

export function useClient(id: string | undefined): UseClientResult {
  const status = useAuthStore((s) => s.status);
  const getById = useClientsStore((s) => s.getById);
  const authed = status === "authenticated";

  const query = useQuery({
    queryKey: queryKeys.client(id ?? ""),
    queryFn: () => clientsApi.get(id as string),
    enabled: authed && !!id,
  });

  const mockClient = id ? getById(id) : undefined;

  if (!authed) return { client: mockClient, isLoading: false, isError: false, source: "mock", error: null };
  if (query.data) return { client: toClient(query.data), isLoading: false, isError: false, source: "api", error: null };
  if (query.isError) return { client: mockClient, isLoading: false, isError: true, source: "mock", error: query.error };
  return { client: undefined, isLoading: query.isLoading, isError: false, source: "api", error: null };
}

/**
 * Create/update/archive/restore. In API mode each call hits the server and
 * invalidates the clients cache; on failure it throws (no silent mock write).
 * In mock mode it mutates the local store.
 */
export function useClientMutations() {
  const status = useAuthStore((s) => s.status);
  const qc = useQueryClient();
  const add = useClientsStore((s) => s.add);
  const updateMock = useClientsStore((s) => s.update);
  const archiveMock = useClientsStore((s) => s.archive);
  const restoreMock = useClientsStore((s) => s.restore);
  const authed = status === "authenticated";

  const invalidate = () => qc.invalidateQueries({ queryKey: CLIENTS_QUERY_PREFIX });

  return {
    mode: (authed ? "api" : "mock") as DataSource,
    create: async (values: ClientFormValues) => {
      if (authed) {
        await clientsApi.create(values);
        await invalidate();
      } else {
        add(values);
      }
    },
    update: async (id: string, values: ClientFormValues) => {
      if (authed) {
        await clientsApi.update(id, values);
        await invalidate();
      } else {
        updateMock(id, values);
      }
    },
    archive: async (id: string) => {
      if (authed) {
        await clientsApi.archive(id);
        await invalidate();
      } else {
        archiveMock(id);
      }
    },
    restore: async (id: string) => {
      if (authed) {
        await clientsApi.restore(id);
        await invalidate();
      } else {
        restoreMock(id);
      }
    },
  };
}
